import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type {AuditLog, ProviderPayoutPreferences, ProviderPlatformFeeModel} from '@gramio/shared';
import {
  SKILL_CATEGORY_IDS_REQUIRING_LICENSE,
  type VerificationDocument,
  type VerificationDocumentType,
} from '@gramio/shared';
import { providerManagementService, PlatformProvider } from '../services/providerManagement';
import './ProviderDetailPage.css';

const GRAMIO_SUPPORT_EMAIL = 'support@gramio.co.in';

type Tab = 'overview' | 'bookings' | 'earnings' | 'reviews' | 'verification' | 'edit';

export const ProviderDetailPage: React.FC = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<PlatformProvider | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookings, setBookings] = useState<unknown[]>([]);
  const [earnings, setEarnings] = useState<{payouts: unknown[]; totalEarnings: number; pendingEarnings: number} | null>(null);
  const [loadingTab, setLoadingTab] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<PlatformProvider>>({});
  const [saving, setSaving] = useState(false);
  const [payoutPrefs, setPayoutPrefs] = useState<ProviderPayoutPreferences | null | undefined>(undefined);
  const [monthlyPlanInput, setMonthlyPlanInput] = useState('');
  const [savingMonthlyPlan, setSavingMonthlyPlan] = useState(false);
  const [adminFeeModel, setAdminFeeModel] = useState<ProviderPlatformFeeModel>('standard');
  const [adminPercentInput, setAdminPercentInput] = useState('');
  const [savingFeeSettings, setSavingFeeSettings] = useState(false);
  const [draftVerificationDocs, setDraftVerificationDocs] = useState<VerificationDocument[]>([]);
  const [licenseBySkillId, setLicenseBySkillId] = useState<Record<string, string>>({});
  const [adminUploadDocType, setAdminUploadDocType] = useState<VerificationDocumentType>('drivers_license');
  const [adminUploadSkillId, setAdminUploadSkillId] = useState('');
  const [adminUploading, setAdminUploading] = useState(false);
  const [savingVerification, setSavingVerification] = useState(false);
  const adminVerificationFileRef = useRef<HTMLInputElement>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [moderationModal, setModerationModal] = useState<null | 'suspend' | 'disable' | 'reactivate'>(null);
  const [moderationReason, setModerationReason] = useState('');
  const [moderationSaving, setModerationSaving] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deactivateAck, setDeactivateAck] = useState(false);
  const [deactivateIdInput, setDeactivateIdInput] = useState('');
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);

  const regulatedSkillIds = useMemo(
    () => (provider?.skills ?? []).filter((s) => SKILL_CATEGORY_IDS_REQUIRING_LICENSE.includes(s)),
    [provider?.skills]
  );

  useEffect(() => {
    if (!provider) return;
    setDraftVerificationDocs(provider.verification?.documents ?? []);
    const next: Record<string, string> = {};
    for (const lic of provider.verification?.professionalLicenses ?? []) {
      next[lic.skillCategoryId] = lic.licenseNumber;
    }
    setLicenseBySkillId(next);
    const regulated = (provider.skills ?? []).filter((s) => SKILL_CATEGORY_IDS_REQUIRING_LICENSE.includes(s));
    if (regulated.length === 1) setAdminUploadSkillId(regulated[0]);
  }, [provider]);

  useEffect(() => {
    if (!providerId) return;

    const loadProvider = async () => {
      try {
        setLoading(true);
        setError(null);
        const providerData = await providerManagementService.getProvider(providerId);
        setProvider(providerData);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load provider');
        console.error('Error loading provider:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProvider();
  }, [providerId]);

  useEffect(() => {
    if (!providerId || !provider) return;
    let cancelled = false;
    (async () => {
      try {
        const prefs = await providerManagementService.getProviderPayoutPreferences(providerId);
        if (!cancelled) {
          setPayoutPrefs(prefs);
          if (prefs) {
            setAdminFeeModel(prefs.platformFeeModel ?? 'standard');
            setAdminPercentInput(
              prefs.perCustomerCommissionPercent != null &&
                Number.isFinite(prefs.perCustomerCommissionPercent)
                ? String(prefs.perCustomerCommissionPercent)
                : ''
            );
            setMonthlyPlanInput(
              prefs.monthlyPlanActiveUntil ? prefs.monthlyPlanActiveUntil.slice(0, 16) : ''
            );
          }
        }
      } catch {
        if (!cancelled) setPayoutPrefs(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [providerId, provider]);

  useEffect(() => {
    if (!providerId || !provider || activeTab === 'overview' || activeTab === 'reviews' || activeTab === 'verification' || activeTab === 'edit') return;

    const loadTabData = async () => {
      try {
        setLoadingTab(true);
        switch (activeTab) {
          case 'bookings': {
            const bookingsData = await providerManagementService.getProviderBookings(providerId);
            setBookings(bookingsData.bookings);
            break;
          }
          case 'earnings': {
            const earningsData = await providerManagementService.getProviderEarnings(providerId);
            setEarnings({
              payouts: earningsData.payouts,
              totalEarnings: earningsData.totalEarnings,
              pendingEarnings: earningsData.pendingEarnings,
            });
            break;
          }
        }
      } catch (err: unknown) {
        console.error(`Error loading ${activeTab}:`, err);
      } finally {
        setLoadingTab(false);
      }
    };

    loadTabData();
  }, [activeTab, providerId, provider]);

  useEffect(() => {
    if (!providerId) return;
    let cancelled = false;
    setAuditLoading(true);
    providerManagementService
      .listProviderAuditLogs(providerId)
      .then((r) => {
        if (!cancelled) setAuditLogs(r.logs);
      })
      .catch(() => {
        if (!cancelled) setAuditLogs([]);
      })
      .finally(() => {
        if (!cancelled) setAuditLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [providerId, provider?.updatedAt]);

  const handleVerify = async () => {
    if (!providerId) return;

    try {
      setVerifying(true);
      const updatedProvider = await providerManagementService.verifyProvider(providerId);
      setProvider(updatedProvider);
      const audit = await providerManagementService.listProviderAuditLogs(providerId);
      setAuditLogs(audit.logs);
    } catch (err: unknown) {
      console.error('Error verifying provider:', err);
      alert('Failed to verify provider: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setVerifying(false);
    }
  };

  const platformVerificationComplete = provider?.verification?.status === 'verified';

  const openDeactivateModal = () => {
    setDeactivateAck(false);
    setDeactivateIdInput('');
    setDeactivateModalOpen(true);
  };

  const closeDeactivateModal = () => {
    if (deactivateSubmitting) return;
    setDeactivateModalOpen(false);
    setDeactivateAck(false);
    setDeactivateIdInput('');
  };

  const submitDeactivateProvider = async () => {
    if (!providerId || deactivateIdInput.trim() !== providerId || !deactivateAck) return;
    setDeactivateSubmitting(true);
    try {
      const updated = await providerManagementService.deleteProvider(providerId);
      setProvider(updated);
      setDeactivateModalOpen(false);
      setDeactivateAck(false);
      setDeactivateIdInput('');
      const audit = await providerManagementService.listProviderAuditLogs(providerId);
      setAuditLogs(audit.logs);
      navigate('/dashboard/providers');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to deactivate provider');
    } finally {
      setDeactivateSubmitting(false);
    }
  };

  const submitModeration = async () => {
    if (!providerId || !moderationModal) return;
    const reason = moderationReason.trim();
    if ((moderationModal === 'suspend' || moderationModal === 'disable') && reason.length < 3) {
      alert('Please enter a reason (at least 3 characters).');
      return;
    }
    setModerationSaving(true);
    try {
      const updated = await providerManagementService.setProviderModeration(providerId, {
        action: moderationModal,
        ...(moderationModal === 'reactivate' ? {} : {reason}),
      });
      setProvider(updated);
      setModerationModal(null);
      setModerationReason('');
      const audit = await providerManagementService.listProviderAuditLogs(providerId);
      setAuditLogs(audit.logs);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setModerationSaving(false);
    }
  };

  const handleAdminVerificationFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !providerId || !provider) return;
    const contentType = file.type;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(contentType)) {
      alert('Please choose a JPEG, PNG, or WebP image.');
      return;
    }
    setAdminUploading(true);
    try {
      const skillCategoryId =
        adminUploadDocType === 'professional_license' ? adminUploadSkillId.trim() : undefined;
      if (adminUploadDocType === 'professional_license' && !skillCategoryId) {
        alert('Select the regulated skill this professional license applies to.');
        return;
      }
      const {uploadUrl, documentUrl} = await providerManagementService.postProviderVerificationUploadUrl(
        providerId,
        {
          contentType,
          documentType: adminUploadDocType,
          ...(skillCategoryId ? {skillCategoryId} : {}),
        }
      );
      const putRes = await fetch(uploadUrl, {method: 'PUT', body: file, headers: {'Content-Type': contentType}});
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      const newDoc: VerificationDocument = {
        documentId: crypto.randomUUID(),
        documentType: adminUploadDocType,
        documentUrl,
        uploadedAt: new Date().toISOString(),
        source: 'admin',
        ...(skillCategoryId ? {skillCategoryId} : {}),
      };
      setDraftVerificationDocs((prev) => [...prev, newDoc]);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setAdminUploading(false);
    }
  };

  const handleSaveVerificationDraft = async () => {
    if (!providerId || !provider) return;
    if (platformVerificationComplete) {
      const ok = window.confirm(
        'This provider is already marked verified. Save anyway to update stored documents?'
      );
      if (!ok) return;
    }
    setSavingVerification(true);
    try {
      const professionalLicenses = regulatedSkillIds.map((skillCategoryId) => ({
        skillCategoryId,
        licenseNumber: (licenseBySkillId[skillCategoryId] ?? '').trim(),
      }));
      const nextStatus =
        provider.verification?.status === 'verified' ? 'verified' : 'pending_review';
      const updated = await providerManagementService.updateProvider(providerId, {
        verification: {
          status: nextStatus,
          documents: draftVerificationDocs,
          professionalLicenses,
          submittedAt: provider.verification?.submittedAt ?? new Date().toISOString(),
          reviewNotes: provider.verification?.reviewNotes,
        },
      });
      setProvider(updated);
      alert('Verification saved.');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to save verification');
    } finally {
      setSavingVerification(false);
    }
  };

  const handleEdit = () => {
    if (provider) {
      setEditForm({
        bio: provider.bio,
        skills: provider.skills,
        serviceTypes: provider.serviceTypes,
        pricing: provider.pricing,
        availability: provider.availability,
        location: {
          address: provider.location?.address ?? '',
          city: provider.location?.city ?? '',
          state: provider.location?.state ?? '',
          pincode: provider.location?.pincode ?? '',
          ...(provider.location?.coordinates ? {coordinates: provider.location.coordinates} : {}),
        },
        experience: provider.experience
          ? {...provider.experience}
          : {yearsOfExperience: 0, description: ''},
        languages: provider.languages ? [...provider.languages] : [],
      });
      setIsEditing(true);
      setActiveTab('edit');
    }
  };

  const setEditLocationField = (
    field: 'address' | 'city' | 'state' | 'pincode',
    value: string
  ) => {
    setEditForm((prev) => ({
      ...prev,
      location: {
        address: '',
        city: '',
        state: '',
        pincode: '',
        ...prev.location,
        [field]: value,
      },
    }));
  };

  const handleSaveMonthlyPlan = async (clear: boolean) => {
    if (!providerId) return;
    if (!clear && !monthlyPlanInput.trim()) {
      alert('Enter a date/time or use Clear plan.');
      return;
    }
    setSavingMonthlyPlan(true);
    try {
      const until = clear ? null : new Date(monthlyPlanInput).toISOString();
      if (!clear && !Number.isFinite(Date.parse(monthlyPlanInput))) {
        alert('Enter a valid date/time for the monthly plan end.');
        return;
      }
      const updated = await providerManagementService.updateProviderPayoutPreferences(providerId, {
        monthlyPlanActiveUntil: until,
      });
      setPayoutPrefs(updated);
      if (updated) {
        setMonthlyPlanInput(
          updated.monthlyPlanActiveUntil ? updated.monthlyPlanActiveUntil.slice(0, 16) : ''
        );
      }
      alert(clear ? 'Monthly unlimited plan cleared.' : 'Monthly unlimited plan updated.');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update plan');
    } finally {
      setSavingMonthlyPlan(false);
    }
  };

  const handleSavePlatformFee = async () => {
    if (!providerId || !payoutPrefs) return;
    if (adminFeeModel === 'per_customer_percent' || adminFeeModel === 'reduced_percent') {
      if (adminPercentInput.trim() !== '') {
        const n = Number(adminPercentInput);
        if (!Number.isFinite(n) || n < 0 || n > 90) {
          alert('Override percent must be between 0 and 90, or leave blank to use dashboard reduced rate.');
          return;
        }
      }
    }
    setSavingFeeSettings(true);
    try {
      const body: {
        platformFeeModel: ProviderPlatformFeeModel;
        perCustomerCommissionPercent?: number | null;
      } = {platformFeeModel: adminFeeModel};
      if (adminFeeModel === 'per_customer_percent' || adminFeeModel === 'reduced_percent') {
        body.perCustomerCommissionPercent =
          adminPercentInput.trim() === '' ? null : Number(adminPercentInput);
      }
      const updated = await providerManagementService.updateProviderPayoutPreferences(providerId, body);
      setPayoutPrefs(updated);
      if (updated) {
        setAdminFeeModel(updated.platformFeeModel ?? 'standard');
        setAdminPercentInput(
          updated.perCustomerCommissionPercent != null &&
            Number.isFinite(updated.perCustomerCommissionPercent)
            ? String(updated.perCustomerCommissionPercent)
            : ''
        );
      }
      alert('Platform fee settings saved.');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to save platform fee');
    } finally {
      setSavingFeeSettings(false);
    }
  };

  const handleSave = async () => {
    if (!providerId) return;

    const loc = editForm.location;
    if (
      !loc?.address?.trim() ||
      !loc.city?.trim() ||
      !loc.state?.trim() ||
      !loc.pincode?.trim()
    ) {
      alert('Location must include address, city, state, and pincode.');
      return;
    }

    try {
      setSaving(true);
      const payload: Partial<PlatformProvider> = {
        ...editForm,
        location: {
          ...loc,
          address: loc.address.trim(),
          city: loc.city.trim(),
          state: loc.state.trim(),
          pincode: loc.pincode.trim(),
          ...(loc.coordinates ? {coordinates: loc.coordinates} : {}),
        },
      };
      const updatedProvider = await providerManagementService.updateProvider(providerId, payload);
      setProvider(updatedProvider);
      setIsEditing(false);
      setActiveTab('overview');
    } catch (err: unknown) {
      console.error('Error updating provider:', err);
      alert('Failed to update provider: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
    setActiveTab('overview');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateStats = () => {
    if (!provider) return { totalBookings: 0, totalEarnings: 0, averageRating: 0 };

    const totalBookings = bookings.length;
    const totalEarnings = earnings?.totalEarnings || 0;
    const averageRating = provider.ratings?.average || 0;

    return { totalBookings, totalEarnings, averageRating };
  };

  if (loading) {
    return <div className="loading">Loading provider...</div>;
  }

  if (error || !provider) {
    return (
      <div className="error-container">
        <div className="error-message">{error || 'Provider not found'}</div>
        <button onClick={() => navigate('/dashboard/providers')} className="back-button">
          Back to Providers
        </button>
      </div>
    );
  }

  const stats = calculateStats();
  const accountStatus = provider.adminAccount?.status ?? 'active';
  const canReactivateModeration = accountStatus === 'suspended' || accountStatus === 'disabled';

  const highLevelLocation =
    provider.location?.city && provider.location?.state
      ? `${provider.location.city}, ${provider.location.state}${
          provider.location.pincode ? ` · ${provider.location.pincode}` : ''
        }`
      : provider.location?.address || 'N/A';

  const serviceTypesShort = (() => {
    const st = provider.serviceTypes ?? [];
    if (st.length === 0) return 'N/A';
    if (st.length <= 4) return st.join(', ');
    return `${st.slice(0, 4).join(', ')} +${st.length - 4} more`;
  })();

  const skillsShort = (() => {
    const sk = provider.skills ?? [];
    if (sk.length === 0) return 'N/A';
    if (sk.length <= 5) return sk.join(', ');
    return `${sk.slice(0, 5).join(', ')} +${sk.length - 5} more`;
  })();

  const bioExcerpt =
    provider.bio && provider.bio.length > 240 ? `${provider.bio.slice(0, 240)}…` : provider.bio;

  return (
    <div className="provider-detail-page">
      <div className="provider-detail-header">
        <button onClick={() => navigate('/dashboard/providers')} className="back-button">
          ← Back to Providers
        </button>
        <h1>Provider Details</h1>
      </div>

      <div className="provider-info-card">
        <div className="provider-info-header">
          <div>
            <h2>{provider.email || 'N/A'}</h2>
            <p className="provider-email">{provider.phone || 'N/A'}</p>
          </div>
          <div className="provider-actions">
            <span className={`status-badge ${provider.availability?.status || 'unavailable'}`}>
              {provider.availability?.status || 'unavailable'}
            </span>
            <span className={`verification-status ${provider.verification?.status || 'unverified'}`}>
              ID: {provider.verification?.status || 'unverified'}
            </span>
            <span className={`account-status-pill account-status-${accountStatus}`}>
              Account: {accountStatus}
            </span>
            {!platformVerificationComplete && (
              <button
                className="verify-button"
                onClick={handleVerify}
                disabled={verifying}
              >
                {verifying ? 'Verifying...' : 'Verify Provider'}
              </button>
            )}
            {platformVerificationComplete && (
              <span className="verified-badge">✓ ID verified</span>
            )}
          </div>
        </div>

        <div className="provider-stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.totalBookings}</div>
            <div className="stat-label">Total Bookings</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">₹{stats.totalEarnings.toLocaleString()}</div>
            <div className="stat-label">Total Earnings</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {stats.averageRating > 0 ? stats.averageRating.toFixed(1) : 'N/A'}
            </div>
            <div className="stat-label">Average Rating</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{provider.ratings?.count || 0}</div>
            <div className="stat-label">Total Reviews</div>
          </div>
        </div>

        <div className="provider-high-level-section">
          <h3 className="high-level-heading">High-level summary</h3>
          <div className="provider-details-grid provider-summary-grid">
            <div className="detail-item">
              <label>Area</label>
              <div>{highLevelLocation}</div>
            </div>
            <div className="detail-item">
              <label>Service types</label>
              <div>{serviceTypesShort}</div>
            </div>
            <div className="detail-item">
              <label>Platform verification</label>
              <div>
                <span className={`verification-status ${provider.verification?.status || 'unverified'}`}>
                  {provider.verification?.status || 'unverified'}
                </span>
              </div>
            </div>
            <div className="detail-item">
              <label>Admin account</label>
              <div>
                <span className={`account-status-pill account-status-${accountStatus}`}>{accountStatus}</span>
                {provider.adminAccount?.reason && accountStatus !== 'active' && (
                  <div className="moderation-reason-text">{provider.adminAccount.reason}</div>
                )}
              </div>
            </div>
            <div className="detail-item">
              <label>Background check</label>
              <div>
                <span className={`verification-status ${provider.professionalInfo?.backgroundCheckStatus || 'pending'}`}>
                  {provider.professionalInfo?.backgroundCheckStatus || 'pending'}
                </span>
              </div>
            </div>
            <div className="detail-item">
              <label>Onboarded</label>
              <div>{formatDate(provider.createdAt)}</div>
            </div>
          </div>
          <details className="technical-ids-block">
            <summary>Technical identifiers and full address</summary>
            <div className="provider-details-grid nested-details-grid">
              <div className="detail-item">
                <label>Provider ID</label>
                <div className="mono-wrap">{provider.providerId}</div>
              </div>
              <div className="detail-item">
                <label>User ID</label>
                <div className="mono-wrap">{provider.userId}</div>
              </div>
              <div className="detail-item detail-item-span">
                <label>Full location</label>
                <div>
                  {provider.location
                    ? `${provider.location.address}, ${provider.location.city}, ${provider.location.state} ${provider.location.pincode}`
                    : 'N/A'}
                </div>
              </div>
            </div>
          </details>
        </div>

        <div className="provider-moderation-section">
          <h3 className="high-level-heading">Moderation</h3>
          <p className="muted small">
            Suspend or disable removes the provider from customer discovery (even if ID-verified). Reactivate restores
            visibility when verification is still <strong>verified</strong>.
          </p>
          <div className="moderation-actions">
            <button
              type="button"
              className="btn-secondary"
              disabled={accountStatus !== 'active' || moderationSaving}
              onClick={() => {
                setModerationModal('suspend');
                setModerationReason('');
              }}
            >
              Suspend
            </button>
            <button
              type="button"
              className="btn-danger-outline"
              disabled={accountStatus !== 'active' || moderationSaving}
              onClick={() => {
                setModerationModal('disable');
                setModerationReason('');
              }}
            >
              Disable
            </button>
            <button
              type="button"
              className="verify-button"
              disabled={!canReactivateModeration || moderationSaving}
              onClick={() => {
                setModerationModal('reactivate');
                setModerationReason('');
              }}
            >
              Reactivate
            </button>
            <button
              type="button"
              className="btn-danger-solid"
              disabled={moderationSaving || deactivateSubmitting}
              onClick={openDeactivateModal}
            >
              Deactivate provider…
            </button>
          </div>
          <p className="muted small" style={{marginTop: 12}}>
            <strong>Deactivate</strong> (separate from suspend/disable) sets availability to unavailable via the admin API.
            Use the confirmation dialog — you must acknowledge and type the provider ID.
          </p>
        </div>

        <div className="provider-audit-section">
          <h3 className="high-level-heading">Admin activity</h3>
          {auditLoading ? (
            <p className="muted">Loading activity…</p>
          ) : auditLogs.length === 0 ? (
            <p className="muted">No audit entries yet for this provider.</p>
          ) : (
            <div className="audit-log-table-wrap">
              <table className="audit-log-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Admin</th>
                    <th>Action</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => {
                    const sub = log.details && typeof log.details.subAction === 'string' ? log.details.subAction : log.actionType;
                    return (
                      <tr key={log.logId}>
                        <td>{formatDate(log.timestamp)}</td>
                        <td>{log.adminEmail}</td>
                        <td>
                          <code className="audit-sub-action">{sub}</code>
                        </td>
                        <td>
                          <pre className="audit-details-pre">{JSON.stringify(log.details ?? {}, null, 2)}</pre>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {payoutPrefs !== undefined && (
          <div className="provider-platform-fee-admin">
            <h3>Platform fee (provider choice)</h3>
            {!payoutPrefs ? (
              <p className="muted">
                No payout preferences yet — provider must save UPI on the mobile app before monthly unlimited can be activated.
              </p>
            ) : (
              <>
                <div className="detail-item">
                  <label>Current model</label>
                  <div>{payoutPrefs.platformFeeModel ?? 'standard'}</div>
                </div>
                {(payoutPrefs.platformFeeModel === 'per_customer_percent' ||
                  payoutPrefs.platformFeeModel === 'reduced_percent') &&
                  payoutPrefs.perCustomerCommissionPercent != null && (
                    <div className="detail-item">
                      <label>Custom commission override</label>
                      <div>{payoutPrefs.perCustomerCommissionPercent}% (admin override)</div>
                    </div>
                  )}
                {payoutPrefs.monthlyPlanActiveUntil && (
                  <div className="detail-item">
                    <label>Monthly unlimited active until</label>
                    <div>{formatDate(payoutPrefs.monthlyPlanActiveUntil)}</div>
                  </div>
                )}
                <div className="platform-fee-admin-form">
                  <h4>Admin override — fee model and percent</h4>
                  <p className="muted small">
                    Same settings as the provider app. Global standard/reduced % and fixed fees are set under System
                    Settings → Platform Configuration (payout policy panel). Monthly subscription revenue stays with
                    Gramio; while active, per-booking commission is 0%.
                  </p>
                  <label htmlFor="admin-fee-model">Platform fee model</label>
                  <select
                    id="admin-fee-model"
                    value={adminFeeModel}
                    onChange={(e) => setAdminFeeModel(e.target.value as ProviderPlatformFeeModel)}
                    disabled={savingFeeSettings}
                  >
                    <option value="standard">Standard (dashboard standard %)</option>
                    <option value="reduced_percent">Reduced rate (dashboard reduced %)</option>
                    <option value="fixed_per_booking">Fixed fee per booking (by work category)</option>
                    <option value="monthly_unlimited">Monthly unlimited (0% per booking while plan active)</option>
                    <option value="per_customer_percent">Legacy: custom % per booking</option>
                  </select>
                  {(adminFeeModel === 'per_customer_percent' || adminFeeModel === 'reduced_percent') && (
                    <>
                      <label htmlFor="admin-fee-percent">Optional override % (0–90, blank = dashboard default)</label>
                      <input
                        id="admin-fee-percent"
                        type="number"
                        min={0}
                        max={90}
                        step={0.1}
                        value={adminPercentInput}
                        onChange={(e) => setAdminPercentInput(e.target.value)}
                        disabled={savingFeeSettings}
                        placeholder="e.g. 10"
                      />
                    </>
                  )}
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={savingFeeSettings}
                    onClick={() => void handleSavePlatformFee()}
                  >
                    {savingFeeSettings ? 'Saving…' : 'Save fee model'}
                  </button>
                </div>
                <p className="muted small">
                  When the provider is on <strong>monthly unlimited</strong>, set the subscription end below after they pay
                  the platform. Until then, standard commission applies unless you chose another model above. While the
                  monthly plan is active, per-booking platform commission is 0%.
                </p>
                <div className="monthly-plan-form">
                  <label htmlFor="monthly-until">Active until (local date/time)</label>
                  <input
                    id="monthly-until"
                    type="datetime-local"
                    value={monthlyPlanInput}
                    onChange={(e) => setMonthlyPlanInput(e.target.value)}
                    disabled={savingMonthlyPlan}
                  />
                  <div className="monthly-plan-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={savingMonthlyPlan}
                      onClick={() => handleSaveMonthlyPlan(false)}
                    >
                      {savingMonthlyPlan ? 'Saving…' : 'Save end date'}
                    </button>
                    <button
                      type="button"
                      className="btn-danger-outline"
                      disabled={savingMonthlyPlan}
                      onClick={() => handleSaveMonthlyPlan(true)}
                    >
                      Clear plan
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            Bookings ({bookings.length})
          </button>
          <button
            className={`tab ${activeTab === 'earnings' ? 'active' : ''}`}
            onClick={() => setActiveTab('earnings')}
          >
            Earnings
          </button>
          <button
            className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews ({provider.ratings?.count || 0})
          </button>
          <button
            className={`tab ${activeTab === 'verification' ? 'active' : ''}`}
            onClick={() => setActiveTab('verification')}
          >
            Verification
          </button>
          <button
            className={`tab ${activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => {
              if (!isEditing) handleEdit();
              else setActiveTab('edit');
            }}
          >
            Edit
          </button>
        </div>

        <div className="tab-content">
          {loadingTab ? (
            <div className="loading">Loading {activeTab}...</div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="overview-content">
                  <h3>Profile snapshot</h3>
                  <p className="overview-hint">
                    Condensed view for triage. Use the <strong>Edit</strong> tab for the full profile and pricing
                    breakdown.
                  </p>
                  <div className="info-grid">
                    <div>
                      <strong>Bio:</strong> {bioExcerpt || 'N/A'}
                    </div>
                    <div>
                      <strong>Skills:</strong> {skillsShort}
                    </div>
                    <div>
                      <strong>Languages:</strong>{' '}
                      {provider.languages && provider.languages.length > 0
                        ? provider.languages.join(', ')
                        : 'N/A'}
                    </div>
                    <div>
                      <strong>Experience:</strong>{' '}
                      {provider.experience?.yearsOfExperience ?? 0} years
                    </div>
                    {provider.pricing && (
                      <div>
                        <strong>Pricing (summary):</strong>{' '}
                        <span>
                          {provider.pricing.hourly != null
                            ? `from ₹${provider.pricing.hourly}/hr`
                            : provider.pricing.daily != null
                              ? `from ₹${provider.pricing.daily}/day`
                              : provider.pricing.weekly != null
                                ? `from ₹${provider.pricing.weekly}/wk`
                                : 'See Edit for full rate card'}
                        </span>
                        <span> · Negotiable: {provider.pricing.negotiable ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'bookings' && (
                <div className="bookings-content">
                  <h3>Provider Bookings</h3>
                  {bookings.length === 0 ? (
                    <div className="empty-state">No bookings found</div>
                  ) : (
                    <div className="data-list">
                      {bookings.map((booking: Record<string, unknown>, index) => (
                        <div key={(booking.bookingId as string) || index} className="data-item">
                          <div className="data-item-header">
                            <strong>Booking ID:</strong> {(booking.bookingId as string) || 'N/A'}
                          </div>
                          <div className="data-item-body">
                            <pre>{JSON.stringify(booking, null, 2)}</pre>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'earnings' && (
                <div className="earnings-content">
                  <h3>Provider Earnings</h3>
                  {earnings && (
                    <>
                      <div className="earnings-summary">
                        <div className="earnings-card">
                          <div className="earnings-label">Total Earnings</div>
                          <div className="earnings-value">₹{earnings.totalEarnings.toLocaleString()}</div>
                        </div>
                        <div className="earnings-card">
                          <div className="earnings-label">Pending Earnings</div>
                          <div className="earnings-value">₹{earnings.pendingEarnings.toLocaleString()}</div>
                        </div>
                      </div>
                      {earnings.payouts.length === 0 ? (
                        <div className="empty-state">No payouts found</div>
                      ) : (
                        <div className="data-list">
                          {earnings.payouts.map((payout: Record<string, unknown>, index) => (
                            <div key={(payout.payoutId as string) || index} className="data-item">
                              <div className="data-item-header">
                                <strong>Payout ID:</strong> {(payout.payoutId as string) || 'N/A'}
                              </div>
                              <div className="data-item-body">
                                <pre>{JSON.stringify(payout, null, 2)}</pre>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="reviews-content">
                  <h3>Provider Reviews</h3>
                  <div className="ratings-summary">
                    <div className="rating-breakdown">
                      <div className="rating-average">
                        <div className="rating-value-large">
                          {provider.ratings?.average?.toFixed(1) || 'N/A'}
                        </div>
                        <div className="rating-count">
                          Based on {provider.ratings?.count || 0} reviews
                        </div>
                      </div>
                      {provider.ratings?.breakdown && (
                        <div className="rating-breakdown-list">
                          {Object.entries(provider.ratings.breakdown)
                            .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
                            .map(([rating, count]) => (
                              <div key={rating} className="rating-breakdown-item">
                                <span>{rating} stars</span>
                                <span>{count} reviews</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'verification' && (
                <div className="verification-content">
                  <h3>Verification Status</h3>
                  <div className="verification-info">
                    <div className="verification-support-callout">
                      <strong>Email workflow</strong>
                      <p>
                        Ask the provider to email copies of their ID and licenses to{' '}
                        <a href={`mailto:${GRAMIO_SUPPORT_EMAIL}`}>{GRAMIO_SUPPORT_EMAIL}</a>
                        . You can attach files received there using{' '}
                        <em>Upload verification image</em> below, edit license numbers if needed, and{' '}
                        <em>Save verification</em> when you want changes stored. <strong>Verify Provider</strong>{' '}
                        is allowed even when documents are incomplete: use that only when you explicitly approve
                        an exception (logs note missing items).
                      </p>
                    </div>
                    <div className="verification-item">
                      <label>Platform verification</label>
                      <div>
                        <span className={`verification-status ${provider.verification?.status || 'unverified'}`}>
                          {provider.verification?.status || 'unverified'}
                        </span>
                        {provider.verification?.submittedAt && (
                          <div className="secondary-text">
                            Submitted: {formatDate(provider.verification.submittedAt)}
                          </div>
                        )}
                        {provider.verification?.reviewedAt && (
                          <div className="secondary-text">
                            Reviewed: {formatDate(provider.verification.reviewedAt)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="verification-item verification-admin-upload">
                      <label>Admin: upload verification image</label>
                      <p className="secondary-text verification-upload-hint">
                        JPEG, PNG, or WebP. For professional license photos, pick the matching regulated skill.
                      </p>
                      <div className="verification-upload-row">
                        <select
                          className="form-input verification-doc-type"
                          value={adminUploadDocType}
                          onChange={(e) =>
                            setAdminUploadDocType(e.target.value as VerificationDocumentType)
                          }
                          disabled={adminUploading}
                        >
                          <option value="drivers_license">Driver license</option>
                          <option value="passport">Passport</option>
                          <option value="aadhaar">Aadhaar</option>
                          <option value="professional_license">Professional license</option>
                          <option value="other">Other</option>
                        </select>
                        {adminUploadDocType === 'professional_license' && regulatedSkillIds.length > 0 && (
                          <select
                            className="form-input verification-skill-select"
                            value={adminUploadSkillId}
                            onChange={(e) => setAdminUploadSkillId(e.target.value)}
                            disabled={adminUploading}
                          >
                            <option value="">Regulated skill…</option>
                            {regulatedSkillIds.map((sid) => (
                              <option key={sid} value={sid}>
                                {sid.replace(/_/g, ' ')}
                              </option>
                            ))}
                          </select>
                        )}
                        <input
                          ref={adminVerificationFileRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="verification-file-input"
                          onChange={(ev) => {
                            void handleAdminVerificationFileChosen(ev);
                          }}
                        />
                        <button
                          type="button"
                          className="button-secondary"
                          disabled={adminUploading}
                          onClick={() => adminVerificationFileRef.current?.click()}
                        >
                          {adminUploading ? 'Uploading…' : 'Choose file & upload'}
                        </button>
                      </div>
                      <button
                        type="button"
                        className="edit-button verification-save-btn"
                        disabled={savingVerification || adminUploading}
                        onClick={() => {
                          void handleSaveVerificationDraft();
                        }}
                      >
                        {savingVerification ? 'Saving…' : 'Save verification to platform'}
                      </button>
                    </div>
                    <div className="verification-item">
                      <label>Documents on file ({draftVerificationDocs.length})</label>
                      {draftVerificationDocs.length === 0 ? (
                        <p className="secondary-text">No documents yet.</p>
                      ) : (
                        <ul className="document-list verification-doc-detail-list">
                          {draftVerificationDocs.map((doc) => {
                            const viewHref = doc.viewUrl ?? doc.documentUrl;
                            return (
                              <li key={doc.documentId}>
                                <strong>{doc.documentType.replace(/_/g, ' ')}</strong>
                                {doc.skillCategoryId ? ` · ${doc.skillCategoryId}` : ''}
                                {doc.source === 'admin' && (
                                  <span className="doc-source-badge"> Admin upload</span>
                                )}
                                {doc.uploadedAt && (
                                  <span className="secondary-text"> · {formatDate(doc.uploadedAt)}</span>
                                )}
                                <div>
                                  <a
                                    href={viewHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="doc-view-link"
                                  >
                                    View file
                                  </a>
                                </div>
                                {doc.authenticity && (
                                  <div className="secondary-text">
                                    Scan: {doc.authenticity.status}
                                    {doc.authenticity.method ? ` (${doc.authenticity.method})` : ''}
                                    {doc.authenticity.summary ? ` — ${doc.authenticity.summary}` : ''}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    {regulatedSkillIds.length > 0 && (
                      <div className="verification-item">
                        <label>License numbers (regulated skills)</label>
                        <div className="verification-license-fields">
                          {regulatedSkillIds.map((sid) => (
                            <div key={sid} className="form-group verification-license-row">
                              <label className="verification-license-label">{sid.replace(/_/g, ' ')}</label>
                              <input
                                type="text"
                                className="form-input"
                                value={licenseBySkillId[sid] ?? ''}
                                onChange={(e) =>
                                  setLicenseBySkillId((prev) => ({
                                    ...prev,
                                    [sid]: e.target.value,
                                  }))
                                }
                                placeholder="Registration / license number"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="verification-item">
                      <label>Background Check Status</label>
                      <div>
                        <span className={`verification-status ${provider.professionalInfo?.backgroundCheckStatus || 'pending'}`}>
                          {provider.professionalInfo?.backgroundCheckStatus || 'pending'}
                        </span>
                      </div>
                    </div>
                    {provider.professionalInfo?.backgroundCheckDate && (
                      <div className="verification-item">
                        <label>Background Check Date</label>
                        <div>{formatDate(provider.professionalInfo.backgroundCheckDate)}</div>
                      </div>
                    )}
                    {provider.professionalInfo?.education && provider.professionalInfo.education.length > 0 && (
                      <div className="verification-item">
                        <label>Education</label>
                        <div>
                          {provider.professionalInfo.education.map((edu, idx) => (
                            <div key={idx} className="education-item">
                              <strong>{edu.degree}</strong> - {edu.institution}
                              {edu.isVerified && <span className="verified-badge-small">✓ Verified</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {provider.certifications && provider.certifications.length > 0 && (
                      <div className="verification-item">
                        <label>Certifications</label>
                        <div>
                          {provider.certifications.map((cert, idx) => (
                            <div key={idx} className="certification-item">
                              <strong>{cert.name}</strong> - {cert.issuer}
                              <div className="secondary-text">
                                Issued: {formatDate(cert.issueDate)}
                                {cert.expiryDate && ` | Expires: ${formatDate(cert.expiryDate)}`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'edit' && (
                <div className="edit-content">
                  <h3>Edit Provider Profile</h3>
                  <div className="edit-form">
                    <div className="form-group">
                      <label>Bio</label>
                      <textarea
                        value={editForm.bio || ''}
                        onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                        rows={4}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Skills (comma-separated)</label>
                      <input
                        type="text"
                        value={editForm.skills?.join(', ') || ''}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          skills: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        })}
                        className="form-input"
                        placeholder="e.g., Cleaning, Cooking, Laundry"
                      />
                    </div>

                    <div className="form-group">
                      <label>Service Types (comma-separated)</label>
                      <input
                        type="text"
                        value={editForm.serviceTypes?.join(', ') || ''}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          serviceTypes: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        })}
                        className="form-input"
                        placeholder="e.g., one-time, weekly, monthly"
                      />
                    </div>

                    <div className="form-group">
                      <label>Hourly Rate (₹)</label>
                      <input
                        type="number"
                        value={editForm.pricing?.hourly || ''}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          pricing: {
                            ...editForm.pricing,
                            hourly: e.target.value ? parseFloat(e.target.value) : undefined,
                            negotiable: editForm.pricing?.negotiable ?? true,
                          }
                        })}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={editForm.pricing?.negotiable ?? true}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            pricing: {
                              ...editForm.pricing,
                              negotiable: e.target.checked,
                            }
                          })}
                        />
                        Negotiable
                      </label>
                    </div>

                    <div className="form-group">
                      <label>Availability Status</label>
                      <select
                        value={editForm.availability?.status || 'available'}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          availability: {
                            ...editForm.availability,
                            status: e.target.value as 'available' | 'busy' | 'unavailable',
                            schedule: editForm.availability?.schedule || [],
                            holidays: editForm.availability?.holidays || [],
                          }
                        })}
                        className="form-input"
                      >
                        <option value="available">Available</option>
                        <option value="busy">Busy</option>
                        <option value="unavailable">Unavailable</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Service address</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Street / area"
                        value={editForm.location?.address ?? ''}
                        onChange={(e) => setEditLocationField('address', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.location?.city ?? ''}
                        onChange={(e) => setEditLocationField('city', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.location?.state ?? ''}
                        onChange={(e) => setEditLocationField('state', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Pincode</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.location?.pincode ?? ''}
                        onChange={(e) => setEditLocationField('pincode', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Years of experience</label>
                      <input
                        type="number"
                        min={0}
                        className="form-input"
                        value={
                          editForm.experience?.yearsOfExperience !== undefined &&
                          editForm.experience.yearsOfExperience !== null
                            ? editForm.experience.yearsOfExperience
                            : ''
                        }
                        onChange={(e) => {
                          const raw = e.target.value;
                          setEditForm({
                            ...editForm,
                            experience: {
                              ...editForm.experience,
                              yearsOfExperience:
                                raw === '' ? 0 : Number.parseInt(raw, 10) || 0,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Experience description</label>
                      <textarea
                        className="form-input"
                        rows={3}
                        value={editForm.experience?.description ?? ''}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            experience: {
                              ...editForm.experience,
                              yearsOfExperience: editForm.experience?.yearsOfExperience ?? 0,
                              description: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Languages (comma-separated)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.languages?.join(', ') ?? ''}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            languages: e.target.value
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean),
                          })
                        }
                      />
                    </div>

                    <div className="form-actions">
                      <button onClick={handleSave} className="save-button" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button onClick={handleCancelEdit} className="cancel-button">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {deactivateModalOpen && (
        <div
          className="moderation-modal-overlay"
          role="presentation"
          onClick={() => !deactivateSubmitting && closeDeactivateModal()}
        >
          <div
            className="moderation-modal deactivate-provider-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deactivate-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="deactivate-modal-title">Deactivate provider profile</h3>
            <p className="muted small">
              This calls the admin <strong>DELETE</strong> action: the provider record stays in the system, but
              availability is set to <strong>unavailable</strong>. Bookings history is unchanged. For policy enforcement
              and discovery removal, also use <strong>Suspend</strong> or <strong>Disable</strong> above when appropriate.
            </p>
            <label className="deactivate-check">
              <input
                type="checkbox"
                checked={deactivateAck}
                onChange={(e) => setDeactivateAck(e.target.checked)}
                disabled={deactivateSubmitting}
              />
              <span>I understand and want to deactivate this provider&apos;s availability.</span>
            </label>
            <label htmlFor="deactivate-id-input">Type the provider ID exactly to confirm</label>
            <input
              id="deactivate-id-input"
              className="moderation-reason-input mono-input"
              type="text"
              autoComplete="off"
              placeholder="Paste provider ID"
              value={deactivateIdInput}
              onChange={(e) => setDeactivateIdInput(e.target.value)}
              disabled={deactivateSubmitting}
            />
            <div className="moderation-modal-actions">
              <button type="button" className="cancel-button" disabled={deactivateSubmitting} onClick={closeDeactivateModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger-outline"
                disabled={
                  deactivateSubmitting ||
                  !deactivateAck ||
                  deactivateIdInput.trim() !== providerId
                }
                onClick={() => void submitDeactivateProvider()}
              >
                {deactivateSubmitting ? 'Deactivating…' : 'Confirm deactivation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {moderationModal && (
        <div
          className="moderation-modal-overlay"
          role="presentation"
          onClick={() => !moderationSaving && setModerationModal(null)}
        >
          <div
            className="moderation-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="moderation-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="moderation-modal-title">
              {moderationModal === 'suspend' && 'Suspend provider'}
              {moderationModal === 'disable' && 'Disable provider'}
              {moderationModal === 'reactivate' && 'Reactivate provider'}
            </h3>
            {(moderationModal === 'suspend' || moderationModal === 'disable') && (
              <>
                <label htmlFor="moderation-reason">Reason (required)</label>
                <textarea
                  id="moderation-reason"
                  className="moderation-reason-input"
                  rows={4}
                  value={moderationReason}
                  onChange={(e) => setModerationReason(e.target.value)}
                  placeholder="Explain why for the audit log…"
                  disabled={moderationSaving}
                />
              </>
            )}
            {moderationModal === 'reactivate' && (
              <p className="muted small">This sets admin account to active and restores discovery if ID verification is verified.</p>
            )}
            <div className="moderation-modal-actions">
              <button
                type="button"
                className="cancel-button"
                disabled={moderationSaving}
                onClick={() => setModerationModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={moderationModal === 'disable' ? 'btn-danger-outline' : 'verify-button'}
                disabled={moderationSaving}
                onClick={() => void submitModeration()}
              >
                {moderationSaving ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

